import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
  SyntheticEvent,
} from "react";
import { Button, Caret } from "@cmsgov/ds-medicare-gov";
import { AppPage } from "../../components";
import AddPrescription from "./AddPrescription";
import EditPrescription from "./EditPrescription";
import ViewPrescriptions from "./ViewPrescriptions";
import { RouteComponentProps } from "react-router";
import routes from "../../app/routes";
import { useTranslate, sendTealiumEvent } from "../../helpers";
import { AppStore } from "../../app/store";
import { PharmacyType } from "../../@types";
import { hasLoginSession } from "../../helpers/loginHelpers";
import { saveBeneDrugs } from "../../api";

export enum PrescriptionsView {
  ADD = "add",
  EDIT = "edit",
  VIEW = "view",
}

const ManagePrescriptionsPage: FunctionComponent<RouteComponentProps> = props => {
  const t = useTranslate();
  const [addedDrug, setAddedDrug] = useState({
    rxcui: "",
    is_generic: false,
    name: "",
  });
  const [mode, setMode] = useState(PrescriptionsView.ADD);
  const [previousMode, setPreviousMode] = useState(PrescriptionsView.ADD);
  const [editPrescriptionIndex, setEditPrescriptionIndex] = useState(-1);
  const [heading, setHeading] = useState("");
  const { state } = useContext(AppStore.AppContext);

  useEffect(() => {
    if (mode === PrescriptionsView.VIEW) {
      setHeading(t("prescription.confirm_your_drug_list"));
    } else if (mode === PrescriptionsView.EDIT) {
      setHeading(t("drug_search.tell_us_about_your_drug"));
    } else {
      setHeading(t("drug_search.add_prescription_drug"));
    }
  }, [mode, state.language]);

  const onSelectDrug = (addedDrug: {
    rxcui: string;
    is_generic: boolean;
    name: string;
  }): void => {
    setPreviousMode(PrescriptionsView.ADD);
    setMode(PrescriptionsView.EDIT);
    setAddedDrug(addedDrug);
  };

  const goToPlans = (): void => {
    const { search } = props.location;

    props.history.push(`${routes.searchResults}${search}`);
  };

  const goToPharmacySelection = (): void => {
    if (hasLoginSession()) {
      saveBeneDrugs(state.prescriptions);
    }

    const { search } = props.location;

    const redirect =
      state.pharmacyType === PharmacyType.MAIL || !state.prescriptions.length
        ? `${routes.searchResults}`
        : `${routes.pharmacy}`;

    props.history.push(`${redirect}${search}`);
  };

  useEffect(() => {
    if (props.location.pathname === routes.prescriptionsList) {
      setMode(PrescriptionsView.VIEW);
    }
  }, [props.location.pathname]);

  const viewPrescriptions = (
    clearRecentlyAdded: SyntheticEvent | boolean = true
  ): void => {
    if (clearRecentlyAdded) {
      setEditPrescriptionIndex(-1);
    }

    setMode(PrescriptionsView.VIEW);
  };

  const addPrescriptions = (): void => {
    setEditPrescriptionIndex(-1);
    setMode(PrescriptionsView.ADD);
  };

  return (
    <AppPage
      heading={heading}
      headerExtension={
        state.prescriptions.length && mode === PrescriptionsView.ADD ? (
          <div className="appPage__header-extension ds-u-padding-bottom--0 ds-u-margin-top--1 ds-u-margin-x--2 ds-u-sm-margin-x--7">
            <Button
              variation="transparent"
              onClick={(): void => {
                sendTealiumEvent("Find Plans - Add Drugs", "Back to Drug List");
                setMode(PrescriptionsView.VIEW);
              }}
              className="ds-c-button--primary-alt ds-u-font-weight--normal ds-u-text-decoration--none ds-u-padding--0"
            >
              <Caret
                left
                className="ds-u-valign--middle ds-u-margin-right--1"
              />
              {t("prescription.back_to_drug_list")}
            </Button>
          </div>
        ) : null
      }
    >
      {mode === PrescriptionsView.ADD && (
        <AddPrescription
          goToPharmacySelection={goToPharmacySelection}
          onSelectDrug={onSelectDrug}
          goToPlans={goToPlans}
        />
      )}

      {mode === PrescriptionsView.EDIT && addedDrug.rxcui && (
        <EditPrescription
          prescriptionIndex={editPrescriptionIndex}
          previousMode={previousMode}
          resetPrescriptionIndex={(): void => setEditPrescriptionIndex(-1)}
          drug={addedDrug}
          viewList={viewPrescriptions}
          goToPharmacySelection={goToPharmacySelection}
          addPrescriptions={addPrescriptions}
          resetModeOnError={(): void => {
            setEditPrescriptionIndex(-1);
            setMode(PrescriptionsView.ADD);
          }}
        />
      )}

      {mode === PrescriptionsView.VIEW && (
        <ViewPrescriptions
          onEdit={(drug: {
            is_generic: boolean;
            rxcui: string;
            index: number;
            name: string;
          }): void => {
            setEditPrescriptionIndex(drug.index);
            setAddedDrug({
              rxcui: drug.rxcui,
              is_generic: drug.is_generic,
              name: drug.name,
            });
            setPreviousMode(PrescriptionsView.VIEW);
            setMode(PrescriptionsView.EDIT);
          }}
          goToPharmacySelection={goToPharmacySelection}
          addMoreDrugs={addPrescriptions}
          resetPrescriptionIndex={(): void => {
            setEditPrescriptionIndex(-1);
          }}
        />
      )}
    </AppPage>
  );
};

export default ManagePrescriptionsPage;
